import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  A2PRegistrationApiService,
  A2PRegistrationInfoInterface,
  A2PRegistrationStatusInterface,
  CreateA2PRegistrationInfoRequestInterface,
  GetA2PRegistrationInfoRequestInterface,
  GetA2PRegistrationInfoResponseInterface,
  GetLatestA2PRegistrationStatusRequestInterface,
  OwnerType,
  UpsertA2PRegistrationInfoRequestInterface,
} from '@vendasta/smsv2';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { SmsRegistrationDependencies } from '../../interface';
import { Region } from './sms-registration-form/types';
import { NORTH_AMERICA_COUNTRIES, RegistrationStage } from './a2p-registration.constants';
import { SmsInjectionToken } from '../../token';
import { getRegistrationStage } from './a2p-helper-functions';
import { OwnerService } from '../../common/owner.service';

export interface A2PRegistrationState {
  ownerId: string;
  a2pRegistrationInfo: A2PRegistrationInfoInterface | null;
  a2pRegistrationStatus: A2PRegistrationStatusInterface;
  RegistrationStage: RegistrationStage;
}

@Injectable({
  providedIn: 'root',
})
export class A2PRegistrationService {
  private readonly A2PRegistrationState$$: BehaviorSubject<A2PRegistrationState> =
    new BehaviorSubject<A2PRegistrationState>({
      ownerId: '',
      a2pRegistrationInfo: null,
      a2pRegistrationStatus: {} as A2PRegistrationStatusInterface,
      RegistrationStage: RegistrationStage.NotSubmitted,
    });

  public readonly A2PRegistrationState$: Observable<A2PRegistrationState> = this.A2PRegistrationState$$.asObservable();

  //TODO(MEGA-2359) this should be proxied through RegistrationService that takes in both the regulation status and the a2pRegistrationStatus
  public readonly registrationStage$: Observable<RegistrationStage> = this.A2PRegistrationState$$.asObservable().pipe(
    map((state) => state.RegistrationStage),
  );

  public readonly a2PRegistrationInfo$: Observable<A2PRegistrationInfoInterface | null> =
    this.A2PRegistrationState$$.pipe(map((state) => state.a2pRegistrationInfo));

  public readonly a2pRegistrationStatus$: Observable<A2PRegistrationStatusInterface> = this.A2PRegistrationState$$.pipe(
    map((state) => state.a2pRegistrationStatus),
  );

  public readonly stateOwnerId$: Observable<string> = this.A2PRegistrationState$$.pipe(
    map((state) => state.ownerId),
    distinctUntilChanged(),
  );

  public readonly showExternalBanner$: Observable<boolean> = this.registrationStage$.pipe(
    map((stage) => stage === RegistrationStage.NotSubmitted || stage === RegistrationStage.DataError),
    shareReplay(1),
  );

  private readonly a2pRegistrationApiService = inject(A2PRegistrationApiService);
  private readonly ownerService = inject(OwnerService);
  private readonly config = inject<SmsRegistrationDependencies>(SmsInjectionToken);

  ownerId$ = this.config.ownerId$.pipe(distinctUntilChanged(), shareReplay(1));
  public ownerType: OwnerType = this.config.ownerType;

  constructor() {
    combineLatest([this.ownerId$, this.config.subaccountID$])
      .pipe(
        switchMap(([ownerId, subaccountId]) => {
          const info$ = this.a2pRegistrationApiService
            .getA2pRegistrationInfo({ ownerId } as GetA2PRegistrationInfoRequestInterface)
            .pipe(
              catchError(() => {
                console.error('Error getting A2P registration info');
                return of({} as GetA2PRegistrationInfoResponseInterface);
              }),
            );

          const status$ =
            !ownerId || !subaccountId
              ? of({} as A2PRegistrationStatusInterface)
              : this.a2pRegistrationApiService
                  .getLatestA2pRegistrationStatus({
                    ownerId,
                    subaccountId,
                  } as GetLatestA2PRegistrationStatusRequestInterface)
                  .pipe(
                    map((response) => response.status),
                    catchError((e) => {
                      console.error('getLatestA2pRegistrationStatus error:', e);
                      return of({} as A2PRegistrationStatusInterface);
                    }),
                  );

          return forkJoin([info$, status$]).pipe(map(([info, status]) => [ownerId, info, status] as const));
        }),
        takeUntilDestroyed(),
      )
      .subscribe(([ownerId, a2pRegistrationInfo, a2pRegistrationStatus]) => {
        const a2pInfo = a2pRegistrationInfo?.a2pRegistrationInfo ?? ({} as A2PRegistrationInfoInterface);

        this.A2PRegistrationState$$.next({
          ownerId,
          a2pRegistrationInfo: a2pInfo,
          a2pRegistrationStatus: a2pRegistrationStatus,
          RegistrationStage: getRegistrationStage(a2pRegistrationStatus, a2pInfo),
        });
      });
  }

  public async upsertA2pRegistration(request: UpsertA2PRegistrationInfoRequestInterface): Promise<HttpResponse<null>> {
    const ownerId = await firstValueFrom(this.ownerId$);
    request.ownerId = ownerId;
    request.ownerType = this.config.ownerType;
    return await firstValueFrom(
      this.a2pRegistrationApiService.upsertA2pRegistrationInfo(request).pipe(
        map((response) => {
          this.updateA2pRegistrationInfo(request);
          return response;
        }),
      ),
    );
  }

  private updateA2pRegistrationInfo(
    a2pRegistrationInfo: CreateA2PRegistrationInfoRequestInterface | UpsertA2PRegistrationInfoRequestInterface,
  ): void {
    const currentState = this.A2PRegistrationState$$.getValue();
    const currentInfo = currentState.a2pRegistrationInfo ?? ({} as A2PRegistrationInfoInterface);
    const updatedInfo = {
      ...currentInfo,
      ...a2pRegistrationInfo,
      submitted: a2pRegistrationInfo.submitted ? new Date() : currentInfo.submitted,
    };
    this.A2PRegistrationState$$.next({
      ...currentState,
      a2pRegistrationInfo: updatedInfo,
      RegistrationStage: getRegistrationStage(currentState.a2pRegistrationStatus, updatedInfo),
    });
  }

  getPrefillA2PFormInfo(): Observable<A2PRegistrationInfoInterface> {
    return this.a2PRegistrationInfo$.pipe(
      filter((info): info is A2PRegistrationInfoInterface => info !== null),
      switchMap((a2PRegistrationInfo) => {
        if (Object.keys(a2PRegistrationInfo).length > 0) {
          return of(a2PRegistrationInfo);
        }

        if (this.ownerType === OwnerType.OWNER_TYPE_ACCOUNT_GROUP) {
          return this.ownerService.getAccountGroupInfo$().pipe(
            map((accountGroupInfo) => {
              return {
                business: {
                  regionOfOperation: NORTH_AMERICA_COUNTRIES.includes(accountGroupInfo?.napData?.country)
                    ? Region.usaAndCanada
                    : '',
                  streetAddress: accountGroupInfo?.napData?.address,
                  streetAddressSecondary: accountGroupInfo?.napData?.address2,
                  state: accountGroupInfo?.napData?.state,
                  city: accountGroupInfo?.napData?.city,
                  postalCode: accountGroupInfo?.napData?.zip,
                  websiteUrl: accountGroupInfo?.napData?.website,
                },
              } as A2PRegistrationInfoInterface;
            }),
          );
        }

        return of({} as A2PRegistrationInfoInterface);
      }),
    );
  }
}
